import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { EndUserComponent, EndUserPages } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import type { FilterMenuItem, SearchFilterDropDownProps } from '../../types';
import localStyles from '../SearchWidgetFilters/SearchWidgetFilters.module.pcss';
import useSearchFilterDropDownMenuItemCountHelper from '../SearchWidgetFilters/useSearchFilterDropDownMenuItemCountHelper';
import useTranslation from '../../../useTranslation';

interface Props extends Omit<SearchFilterDropDownProps, 'filterMenuItems'> {
  /**
   * The dropdown menu item
   */
  menuItem: FilterMenuItem;
  /**
   * Weather to disable the menu item or not.
   */
  isDisabled?: boolean;
}

/**
 * This component returns the dropdown menu items. Also, it can pass the count of the number of search results
 * against the menu item when useCounts flag is enabled
 *
 * @author Akash Goenka
 */
const SearchFilterDropDownItem: React.FC<React.PropsWithChildren<Props>> = ({
  filterName,
  menuItem,
  onChange,
  routerParam,
  activeFilterMenuItem = '',
  useCounts = false,
  contextNode,
  author,
  isDisabled
}) => {
  const { router } = useEndUserRoutes();
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.SEARCH_FILTER_DROPDOWN_ITEM
  );
  const cx = useClassNameMapper(localStyles);
  const { authUser } = useContext(AppContext);

  const menuItemCountResult = useSearchFilterDropDownMenuItemCountHelper(
    { ...router.query },
    routerParam,
    menuItem,
    authUser,
    isDisabled || !useCounts,
    contextNode,
    author
  );

  if (textLoading) {
    return null;
  }

  const itemCount = menuItemCountResult.data?.messageSearch.results.totalCount;

  /**
   * Handle the selected filter item and update the query parameter
   *
   * @param item The selected filter item
   */
  async function handleFilterMenuItemClick(item: FilterMenuItem): Promise<void> {
    onChange();
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        ...router.query,
        [routerParam]: item
      },
      { shallow: true }
    );
  }

  return (
    <Dropdown.Item
      active={menuItem === activeFilterMenuItem}
      onSelect={(): Promise<void> => handleFilterMenuItemClick(menuItem)}
      data-testid={`SearchPage.Dropdown.${filterName}.Item`}
      disabled={isDisabled || menuItemCountResult.data?.messageSearch.results.totalCount === 0}
      className={cx('lia-dropdown-item')}
    >
      <span className={cx('lia-menu-item-content')}>{formatMessage(menuItem)}</span>
      {useCounts && menuItemCountResult.data?.messageSearch.results.totalCount !== 0 && (
        <span
          className={cx('lia-menu-item-count')}
          aria-label={formatMessage('results', { count: itemCount })}
        >
          {itemCount}
        </span>
      )}
    </Dropdown.Item>
  );
};

export default SearchFilterDropDownItem;
